/**
 * شاشة «مكتبي» — أهم شاشة في السيستم (design/Main.dc.html).
 *
 * الحلقة اللي بتخدمها: مهمة تظهر ⇒ حد **يستلمها** ⇒ تتقفل **بدليل** ⇒
 * الخطوة اللي بعدها تتفتح عند القسم التالي.
 *
 * الشكل: صف إحصاء (3 بطاقات بتشتغل كفلاتر) · عمود المهام بالترتيب مع
 * فلاتر المصدر · عمود جانبي (تسليمات وصلتني · الرسائل الجاهزة ·
 * متأخراتي).
 *
 * قواعد منفَّذة هنا حرفيًا:
 *   ⛔ **فعل أساسي كحلي واحد بس في الشاشة** — أول مهمة قابلة للفعل في
 *      الصفحة، وبينتقل لزرار «اقفل المهمة» ساعة ما تفتح لوحة الإقفال.
 *   ⛔ **ممنوع تضارب أرقام**: عدّادات فلاتر المصدر بتتحسب من **نفس
 *      المصفوفة المعروضة**، فمجموعها = «الكل» دايمًا. ولو السيستم رجّع
 *      عدد مختلف عن اللي وصل فعلًا، بيتسجّل ERROR وبيبان سطر توضيحي.
 *   ⛔ **ممنوع إقفال أعمى**: زرار الإقفال مقفول لحد ما يكون فيه ملاحظة
 *      إقفال أو بنود القايمة المطلوبة كلها مكتملة.
 *   ⛔ **ممنوع فشل صامت**: كل طلب بيفشل بيتسجّل ERROR (في `api/client`)
 *      وبيبان للموظف — إما حالة شاشة أو سطر أحمر جوّه الكارت نفسه.
 *
 * 🔴 اللي اتضاف بعد فحص «أفعال شغّالة ومالهاش شاشة»:
 *   • **شريحة «طابور فريقي»** — المهام غير الموزّعة في قسمي. قبل كده
 *     ماكانش فيه أي شاشة يشوف فيها الموظف شغل فريقه قبل ما يتسند لحد.
 *   • **«وزّع مهمة»** للمنسق والمدير — الشخص من قسمي بس.
 *   • أفعال الكارت الأربعة اللي كانت موجودة في الـAPI وصفر نداء من هنا:
 *     «اشتغلت فيها» · «أنا مستني حد» · «سلّم لحد» · «ألغِ».
 *
 * ⚠️ الصلاحيات هنا **تحسين تجربة مش أمان** — المنع الحقيقي في
 *    `require_perm` + `scope_filter` جوّه الاستعلام. إخفاء الزرار مش عزل.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import { Icon } from "@/components/Icon";
import { Link } from "@/lib/router";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoPermissionState,
  NoResultsState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { ApiError, OfflineError, errorMessageAr } from "@/api/client";
import {
  DEPT_OPTIONS,
  PRIORITY_OPTIONS,
  SENT_VIA_OPTIONS,
  SLA_OPTIONS,
  blockTask,
  cancelTask,
  claimTask,
  completeTask,
  createTask,
  deptLabelAr,
  handoffTask,
  listAssignable,
  listHandoffs,
  listOverdue,
  listQueue,
  listReadyDrafts,
  listTasks,
  markDraftSent,
  newIdempotencyKey,
  noteProgress,
  nudgeTask,
  setChecklistItem,
  taskIdOfLink,
  undoComplete,
  type AssignablePerson,
  type DeskTask,
  type Handoff,
  type OutboundDraft,
  type SentVia,
  type TaskCounts,
  type TaskMutation,
  type TaskPage,
  type TaskPriority,
  type TaskSource,
} from "@/api/tasks";
import { useSession } from "@/lib/session";
import { fmtDate, fmtNum, fmtRelative } from "@/lib/format";
import "@/styles/desk.css";

/** بنجيب مهامي كلها مرة واحدة عشان العدّادات والبحث يبقوا على نفس الداتا */
const FETCH_LIMIT = 100;
/** الصفحة في العرض — زي التصميم: «عرض 1–5 من 7» */
const PER_PAGE = 5;
/** أقصى مدة يفضل فيها شريط التأكيد لو السيستم ما حددش نافذة تراجع */
const TOAST_MS = 8000;

type StatKey = "open" | "overdue" | "waiting" | "queue";
type SourceFilter = "all" | TaskSource;

/** أفعال الكارت الجانبية — كل واحد بيفتح لوحة صغيرة جوّه الكارت */
type PanelKind = "progress" | "block" | "handoff" | "cancel";

type Panel = {
  taskId: string;
  kind: PanelKind;
  text: string;
  /** وجهة التسليم — للنوع `handoff` بس */
  toUserId: string | null;
};

/** كون واحد من التلاتة — إما وصل، أو فشل برسالة صريحة (مفيش صفر كذّاب) */
type Universe =
  | { ok: true; tasks: DeskTask[]; total: number }
  | { ok: false; messageAr: string };

type DeskData = {
  open: Universe;
  overdue: Universe;
  waiting: Universe;
  /** 🔑 طابور فريقي — المهام غير الموزّعة في أقسامي */
  queue: Universe;
  /**
   * «في الطابور من 25 دقيقة» لكل عنصر.
   * ⚠️ النص **بيتولّد في السيستم** زي `sla_label_ar` — بنخزّنه جنب الكون
   *    بدل ما نحطه على `DeskTask` (اللي لازم يفضل مطابق لـ`serialize_task`).
   */
  queueLabels: Map<string, string>;
  counts: TaskCounts | null;
};

type Fatal = { kind: "error" | "offline" | "forbidden"; messageAr: string };

type Note = { tone: "ok" | "error"; text: string };

type SideLoad<T> = { ok: true; rows: T[] } | { ok: false; messageAr: string } | null;

const STAT_CARDS: { key: StatKey; label: string }[] = [
  { key: "open", label: "مهام مفتوحة" },
  { key: "overdue", label: "متأخرة" },
  { key: "waiting", label: "مستنية غيري" },
  { key: "queue", label: "طابور فريقي" },
];

/** أقل عدد حروف لأي سبب/ملاحظة — نفس اللي السيستم بيفرضه */
const MIN_REASON = 2;

/** ترتيب فلاتر المصدر زي التصميم — و«رسالة من معلم» بتظهر لما يكون عندها عدد */
const SOURCE_FILTERS: { key: TaskSource; label: string }[] = [
  { key: "journey", label: "من الرحلة" },
  { key: "event", label: "تلقائية" },
  { key: "assigned", label: "موكّلة ليّ" },
  { key: "self", label: "أضفتها" },
  { key: "telegram", label: "رسالة من معلم" },
];

export function Desk() {
  const { me } = useSession();
  const perms = useMemo(() => new Set(me?.permissions ?? []), [me]);
  // ⛔ المراقب مستثنى صراحةً حتى لو المفتاح موجود — «حسابك للاطلاع بس»
  const isObserver = me?.user.role === "observer";
  const canWrite = perms.has("task.write") && !isObserver;
  const canAssign = perms.has("task.create_for_others") && !isObserver;
  const canCancel = perms.has("task.cancel") && !isObserver;
  const myId = me?.user.id ?? null;
  /** أقسام صندوق التوزيع — المدير نطاقه الأكاديمية كلها */
  const assignDepts = useMemo(() => {
    if (me?.user.role === "director") return DEPT_OPTIONS.map((d) => d.key);
    const mine = me?.departments ?? [];
    if (mine.length > 0) return mine;
    return me?.user.department ? [me.user.department] : [];
  }, [me]);

  const [data, setData] = useState<DeskData | null>(null);
  const [fatal, setFatal] = useState<Fatal | null>(null);
  const [loading, setLoading] = useState(true);
  const [handoffs, setHandoffs] = useState<SideLoad<Handoff>>(null);
  const [drafts, setDrafts] = useState<SideLoad<OutboundDraft>>(null);

  const [stat, setStat] = useState<StatKey>("open");
  const [source, setSource] = useState<SourceFilter>("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState<Record<string, Note>>({});
  const [closing, setClosing] = useState<{ taskId: string; note: string } | null>(null);
  const [adding, setAdding] = useState(false);
  //: بيخلّي دوسة كارت الإحصاء تنزّل على القايمة اللي اتفلترت
  const listRef = useRef<HTMLDivElement | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [panel, setPanel] = useState<Panel | null>(null);
  /** وجهات التسليم — بتتحمّل أول ما حد يفتح لوحة «سلّم لحد» */
  const [people, setPeople] = useState<SideLoad<AssignablePerson>>(null);
  const [toast, setToast] = useState<{ text: string; undoTaskId: string | null } | null>(null);

  /** مفتاح منع التكرار لكل مهمة — نفس الضغطة تعيد نفس المفتاح */
  const claimKeys = useRef<Map<string, string>>(new Map());
  /**
   * ⚠️ القفل ضد الدوس المزدوج **لازم يبقى متزامن**: ضغطتين في نفس اللحظة
   *    بيقروا نفس حالة الـstate القديمة (لسه ما اتحدّثتش) فالاتنين بيعدّوا
   *    ويبعتوا طلبين. الـref بيتغيّر في نفس اللحظة فالضغطة التانية بتترفض.
   */
  const inFlight = useRef<Set<string>>(new Set());
  /** اللي قفلتها — بلاغ الفريق: «كتبت تم، ألاقيها فين؟ بتتمسح؟» */
  const [closed, setClosed] = useState<{ ok: true; tasks: DeskTask[] } | { ok: false } | null>(null);
  const toastTimer = useRef<number | null>(null);

  /* ───────────────────────── تحميل الداتا ───────────────────────── */

  const loadTasks = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    const results = await Promise.allSettled([
      listTasks({ scope: "me", withCounts: true, perPage: FETCH_LIMIT, ...(signal ? { signal } : {}) }),
      listOverdue(signal),
      listTasks({ scope: "blocked_by_others", perPage: FETCH_LIMIT, ...(signal ? { signal } : {}) }),
      // بلا `dept` — السيستم بيرجّع طابور كل أقسامي، مش القسم الأساسي بس
      listQueue(null, signal),
      listTasks({ scope: "me", state: "done", perPage: 30, ...(signal ? { signal } : {}) }),
    ]);
    if (signal?.aborted) return;
    const [openRes, overdueRes, waitingRes, queueRes, closedRes] = results;
    setClosed(closedRes.status === "fulfilled" ? { ok: true, tasks: closedRes.value.tasks } : { ok: false });

    if (openRes.status === "rejected") {
      if (isAbort(openRes.reason)) return;
      setData(null);
      setFatal(classify(openRes.reason));
      setLoading(false);
      return;
    }
    const queueLabels = new Map<string, string>();
    let queue: Universe;
    if (queueRes.status === "fulfilled") {
      for (const t of queueRes.value.tasks) queueLabels.set(t.id, t.waiting_label_ar);
      queue = {
        ok: true,
        tasks: queueRes.value.tasks,
        total: queueRes.value.meta?.total ?? queueRes.value.tasks.length,
      };
    } else {
      // ⛔ ممنوع طابور فاضي كذّاب — الشريحة بتقول السبب
      queue = { ok: false, messageAr: errorMessageAr(queueRes.reason) };
    }
    setFatal(null);
    setData({
      open: { ok: true, tasks: openRes.value.tasks, total: openRes.value.meta?.total ?? openRes.value.tasks.length },
      overdue: toUniverse(overdueRes),
      waiting: toUniverse(waitingRes),
      queue,
      queueLabels,
      counts: openRes.value.counts,
    });
    setLoading(false);
  }, []);

  /**
   * وجهات التسليم — طلب واحد بيتكاش في الشاشة.
   * ⛔ ممنوع نفتح لوحة «سلّم لحد» وفيها قايمة فاضية بلا سبب.
   */
  const loadPeople = useCallback(async () => {
    try {
      setPeople({ ok: true, rows: await listAssignable(null) });
    } catch (err) {
      if (isAbort(err)) return;
      setPeople({ ok: false, messageAr: errorMessageAr(err) });
    }
  }, []);

  const loadHandoffs = useCallback(async (signal?: AbortSignal) => {
    try {
      const rows = await listHandoffs(signal);
      if (!signal?.aborted) setHandoffs({ ok: true, rows });
    } catch (err) {
      if (isAbort(err) || signal?.aborted) return;
      // ⛔ ممنوع نبلع الفشل: القسم بيقول السبب بدل ما يفضل فاضي كأن مفيش تسليمات
      setHandoffs({ ok: false, messageAr: errorMessageAr(err) });
    }
  }, []);

  const loadDrafts = useCallback(async (signal?: AbortSignal) => {
    try {
      const rows = await listReadyDrafts(5, signal);
      if (!signal?.aborted) setDrafts({ ok: true, rows });
    } catch (err) {
      if (isAbort(err) || signal?.aborted) return;
      setDrafts({ ok: false, messageAr: errorMessageAr(err) });
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void loadTasks(ctrl.signal);
    void loadHandoffs(ctrl.signal);
    void loadDrafts(ctrl.signal);
    return () => ctrl.abort();
  }, [loadTasks, loadHandoffs, loadDrafts]);

  useEffect(() => {
    return () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    };
  }, []);

  /** تحديث هادي بعد أي فعل — عشان العدّادات ما تفضلش على قيم قديمة */
  const refresh = useCallback(() => {
    void loadTasks();
    void loadHandoffs();
  }, [loadTasks, loadHandoffs]);

  /* ───────────────────────── الفلترة والعرض ───────────────────────── */

  const universe: Universe | null = data ? data[stat] : null;

  const searched = useMemo(() => {
    if (!universe || !universe.ok) return [];
    const q = normalizeAr(query);
    if (!q) return universe.tasks;
    return universe.tasks.filter((t) =>
      normalizeAr(`${t.title_ar} ${t.body_ar} ${t.entity.label_ar}`).includes(q),
    );
  }, [universe, query]);

  /** ⚠️ العدّادات من نفس المصفوفة المعروضة ⇒ مجموع الفلاتر = «الكل» دايمًا */
  const sourceCounts = useMemo(() => {
    const c: Partial<Record<TaskSource, number>> = {};
    for (const t of searched) c[t.source] = (c[t.source] ?? 0) + 1;
    return c;
  }, [searched]);

  const visible = useMemo(
    () => (source === "all" ? searched : searched.filter((t) => t.source === source)),
    [searched, source],
  );

  const totalPages = Math.max(1, Math.ceil(visible.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PER_PAGE;
  const pageTasks = visible.slice(pageStart, pageStart + PER_PAGE);

  /** لوحة الإقفال بتخص مهمة ظاهرة دلوقتي — غير كده الفعل الأساسي بيرجع للقايمة */
  const closingTask = closing ? (pageTasks.find((t) => t.id === closing.taskId) ?? null) : null;

  /** ⛔ الفعل الكحلي الوحيد: أول مهمة قابلة للفعل، وبيروح للوحة الإقفال لو مفتوحة */
  const primaryTaskId = closingTask
    ? null
    : (pageTasks.find((t) => t.primary_action && t.state !== "blocked")?.id ?? null);

  const openCount = data?.open.ok ? data.open.tasks.length : null;
  const overdueCount = data?.overdue.ok ? data.overdue.tasks.length : null;

  /**
   * ⚠️ حارس تضارب أرقام: لو عدّاد السيستم مش مطابق للّي وصل فعلًا، ده
   *    عطل — بنصرخ في اللوج وبنعرض اللي وصل (عشان الشاشة تفضل متسقة مع
   *    نفسها) بدل ما نعرض رقمين مختلفين لنفس الحاجة.
   */
  useEffect(() => {
    if (!data || !data.open.ok || !data.counts) return;
    const loaded = data.open.tasks.length;
    if (data.open.total <= FETCH_LIMIT && data.counts.all !== loaded) {
      console.error(
        `[desk] عدّاد المهام المفتوحة (${data.counts.all}) مش مطابق للّي وصل (${loaded})`,
      );
    }
  }, [data]);

  const truncated = universe?.ok ? universe.total > universe.tasks.length : false;

  function pick(next: StatKey) {
    setStat(next);
    setPage(1);
    setClosing(null);
    setPanel(null);
    // 🔑 **الكارت زرار من الأول — بس أثره بيحصل تحت خط النظر.**
    //    المشرفة دوست «متأخرة» وسألت «فيه إمكانية يحوّلني ليها تلقائي؟» —
    //    القايمة كانت بتتفلتر فعلًا، هي بس ما شافتش حاجة اتحركت.
    //    ⛔ الحل مش رسالة «القايمة اتفلترت»: الحركة نفسها هي اللي بتقول.
    requestAnimationFrame(() => {
      listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function pickSource(next: SourceFilter) {
    setSource(next);
    setPage(1);
  }

  /* ───────────────────────── الأفعال ───────────────────────── */

  /** بيرجّع false لو فيه طلب شغّال بنفس المفتاح — نداء واحد بس بيعدّي */
  function lock(key: string): boolean {
    if (inFlight.current.has(key)) return false;
    inFlight.current.add(key);
    setBusy((b) => ({ ...b, [key]: true }));
    return true;
  }

  function unlock(key: string) {
    inFlight.current.delete(key);
    setBusy((b) => ({ ...b, [key]: false }));
  }

  function setNote(id: string, note: Note | null) {
    setNotes((prev) => {
      const next = { ...prev };
      if (note) next[id] = note;
      else delete next[id];
      return next;
    });
  }

  function showToast(text: string, undoTaskId: string | null, undoUntil?: string | null) {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast({ text, undoTaskId });
    const until = undoUntil ? new Date(undoUntil).getTime() - Date.now() : TOAST_MS;
    const ms = Math.min(Math.max(Number.isFinite(until) ? until : TOAST_MS, 2000), 60_000);
    toastTimer.current = window.setTimeout(() => setToast(null), ms);
  }

  /** بيبدّل مهمة واحدة في كل الأكوان — من غير إعادة تحميل الشاشة */
  function replaceTask(task: DeskTask) {
    setData((prev) => {
      if (!prev) return prev;
      const swap = (u: Universe): Universe =>
        u.ok ? { ...u, tasks: u.tasks.map((t) => (t.id === task.id ? task : t)) } : u;
      return {
        ...prev,
        open: swap(prev.open),
        overdue: swap(prev.overdue),
        waiting: swap(prev.waiting),
        queue: swap(prev.queue),
      };
    });
  }

  async function runAction(task: DeskTask) {
    const action = task.primary_action;
    if (!action) return;
    if (action.key === "complete") {
      setClosing({ taskId: task.id, note: "" });
      setPanel(null); // لوحة واحدة مفتوحة في المرة
      setNote(task.id, null);
      return;
    }
    if (!lock(task.id)) return; // قفل ضد الدوس المزدوج
    setNote(task.id, null);
    try {
      if (action.key === "claim") {
        let key = claimKeys.current.get(task.id);
        if (!key) {
          key = newIdempotencyKey();
          claimKeys.current.set(task.id, key);
        }
        const res = await claimTask(task.id, task.version, key);
        claimKeys.current.delete(task.id); // خلصت — أي استلام جديد له مفتاحه
        replaceTask(res.task);
        showToast(res.toastAr ?? "استلمتها — بقت مسؤوليتك دلوقتي.", null);
      } else {
        const res = await nudgeTask(task.id);
        replaceTask(res.task);
        showToast(res.toastAr ?? "التذكير اتبعت.", null);
      }
      refresh();
    } catch (err) {
      handleActionError(task, err);
    } finally {
      unlock(task.id);
    }
  }

  /**
   * ⚠️ «حد تاني استلمها» مش خطأ غامض: السيستم بيرجّع اسم اللي استلمها
   *    ووقتها، فبنعرضهم في الكارت ونحدّث الداتا عشان الزرار يعكس الحقيقة.
   */
  function handleActionError(task: DeskTask, err: unknown) {
    if (isAbort(err)) return;
    if (err instanceof ApiError && err.code === "TASK_ALREADY_CLAIMED") {
      const at = err.details?.["claimed_at"];
      const when = typeof at === "string" ? ` — ${fmtRelative(at)}` : "";
      setNote(task.id, { tone: "error", text: `${trimDot(err.messageAr)}${when}` });
      refresh();
      return;
    }
    setNote(task.id, { tone: "error", text: errorMessageAr(err) });
    // ⚠️ 409/410 معناها إن النسخة اللي في إيدي قديمة (حد عدّل أو الحالة اتغيّرت)
    //    — بنجيب الصح فورًا عشان الزرار ما يفضلش بيوعد بحاجة مش ممكنة.
    if (err instanceof ApiError && (err.status === 409 || err.status === 410)) refresh();
  }

  async function confirmClose(task: DeskTask) {
    if (!closing || closing.taskId !== task.id) return;
    const note = closing.note.trim();
    if (!canClose(task, note)) return; // الزرار مقفول أصلًا — ده حزام أمان
    if (!lock(task.id)) return;
    setNote(task.id, null);
    try {
      const res = await completeTask(task.id, note, task.version);
      setClosing(null);
      showToast(res.toastAr ?? "اتقفلت.", task.id, res.task.undo_expires_at);
      refresh();
      void loadDrafts();
    } catch (err) {
      handleActionError(task, err);
    } finally {
      unlock(task.id);
    }
  }

  async function undoLast(taskId: string) {
    if (!lock(taskId)) return;
    try {
      const res = await undoComplete(taskId);
      showToast(res.toastAr ?? "رجّعناها — المهمة تاني عندك.", null);
      refresh();
    } catch (err) {
      if (!isAbort(err)) showToast(errorMessageAr(err), null);
    } finally {
      unlock(taskId);
    }
  }

  async function toggleChecklist(task: DeskTask, key: string, done: boolean) {
    const busyKey = `${task.id}:${key}`;
    if (!lock(busyKey)) return;
    try {
      const updated = await setChecklistItem(task.id, key, done);
      replaceTask(updated);
    } catch (err) {
      if (!isAbort(err)) setNote(task.id, { tone: "error", text: errorMessageAr(err) });
    } finally {
      unlock(busyKey);
    }
  }

  /**
   * «+ مهمة» — لنفسي، وبمهلة وأولوية زيّها زيّ أي مهمة السيستم بيولّدها.
   *
   * 🔑 المهلة **مش زينة**: المهمة بلا `due_at` ما بتظهرش في «المتأخر» ولا
   *    بتترتّب في الطابور — يعني بتقعد للأبد وصاحبها نفسه مش شايفها اتأخرت.
   *    عشان كده الافتراضي «خلال يوم» مش «بلا مهلة».
   */
  async function addTask(
    titleAr: string,
    bodyAr: string,
    priority: TaskPriority,
    slaHours: number | null,
  ) {
    const created = await createTask({ titleAr, bodyAr, priority, slaHours });
    setAdding(false);
    showToast(`المهمة «${created.title_ar}» اتضافت في مكتبك.`, null);
    refresh();
  }

  /** «وزّع مهمة» — الشخص من قسمي، والسيستم بيفحص ده تاني عنده */
  async function assignTask(input: {
    titleAr: string;
    bodyAr: string;
    assigneeId: string;
    assigneeName: string;
    dept: string;
    priority: TaskPriority;
    slaHours: number | null;
  }) {
    const created = await createTask({
      titleAr: input.titleAr,
      bodyAr: input.bodyAr,
      assigneeId: input.assigneeId,
      dept: input.dept,
      priority: input.priority,
      slaHours: input.slaHours,
    });
    setAssigning(false);
    showToast(
      `المهمة «${created.title_ar}» راحت لـ${input.assigneeName} — هتبقى مسؤوليته أول ما يستلمها.`,
      null,
    );
    refresh();
  }

  /** بيفتح لوحة الفعل — والتسليم بيجيب الوجهات لو لسه ما جتش */
  function openPanel(task: DeskTask, kind: PanelKind) {
    setClosing(null);
    setNote(task.id, null);
    setPanel({ taskId: task.id, kind, text: "", toUserId: null });
    if (kind === "handoff" && people === null) void loadPeople();
  }

  /**
   * تنفيذ الفعل المفتوح. الأربعة بيمشوا بنفس القفل التفاؤلي (`version`)
   * وبنفس القفل ضد الدوس المزدوج.
   */
  async function submitPanel(task: DeskTask) {
    if (!panel || panel.taskId !== task.id) return;
    const text = panel.text.trim();
    if (!panelReady(panel, text)) return; // الزرار مقفول أصلًا — ده حزام أمان
    if (!lock(task.id)) return;
    setNote(task.id, null);
    try {
      let res: TaskMutation;
      if (panel.kind === "progress") {
        res = await noteProgress(task.id, text, task.version);
      } else if (panel.kind === "block") {
        res = await blockTask(task.id, text, task.version);
      } else if (panel.kind === "handoff") {
        // ⛔ الحارس ده مش تجميلي: بلا وجهة الطلب بيترفض من السيستم
        if (!panel.toUserId) return;
        res = await handoffTask(task.id, panel.toUserId, text, task.version);
      } else {
        res = await cancelTask(task.id, text, task.version);
      }
      setPanel(null);
      replaceTask(res.task);
      showToast(res.toastAr ?? "اتسجّل.", null);
      refresh();
    } catch (err) {
      handleActionError(task, err);
    } finally {
      unlock(task.id);
    }
  }

  /* ───────────────────────── العرض ───────────────────────── */

  const crumb = (
    <>
      <span className="num">{fmtNum(openCount ?? 0)}</span> مهام ·{" "}
      <span className="num">{fmtNum(overdueCount ?? 0)}</span> متأخرة · {fmtDate(new Date())}
    </>
  );

  return (
    <Shell
      title="مكتبي"
      crumb={crumb}
      search={{
        value: query,
        onChange: (v) => {
          setQuery(v);
          setPage(1);
        },
        // ⚠️ البحث هنا في مهامي اللي على الشاشة — النص بيقول كده بالظبط
        placeholder: "دوّر في مهامي بالعنوان أو اسم الأسرة…",
      }}
    >
      {/* ===== (1) صف الإحصاء — بيشتغل كفلاتر ===== */}
      <div className="stats c4">
        {STAT_CARDS.map((card) => {
          const u = data ? data[card.key] : null;
          const value = u && u.ok ? u.tasks.length : null;
          return (
            <StatCard
              key={card.key}
              value={value}
              label={card.label}
              loading={loading && !data}
              failed={Boolean(u && !u.ok)}
              on={stat === card.key}
              onClick={() => pick(card.key)}
            />
          );
        })}
      </div>

      <div className="desk-cols">
        {/* ===== (2) العمود الرئيسي: مهامي بالترتيب ===== */}
        <div className="desk-main" ref={listRef}>
          <div className="sec-h">
            <span className="sec-title">{SECTION_TITLES[stat]}</span>
            {stat === "queue" ? (
              <span className="sec-note">الاستلام بيثبّت المسؤولية</span>
            ) : null}
            <div className="filters">
              <button
                type="button"
                className={`filt${source === "all" ? " on" : ""}`}
                aria-pressed={source === "all"}
                onClick={() => pickSource("all")}
              >
                الكل <span className="num">{fmtNum(searched.length)}</span>
              </button>
              {SOURCE_FILTERS.filter(
                // «رسالة من معلم» مصدر حقيقي بس نادر — بيظهر لما يكون عنده عدد،
                // وبكده مجموع الشرايح يفضل مساوي لـ«الكل».
                (f) => f.key !== "telegram" || (sourceCounts.telegram ?? 0) > 0,
              ).map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`filt${source === f.key ? " on" : ""}`}
                  aria-pressed={source === f.key}
                  onClick={() => pickSource(f.key)}
                >
                  {f.label} <span className="num">{fmtNum(sourceCounts[f.key] ?? 0)}</span>
                </button>
              ))}
            </div>
            {/* ⛔ **كان `ghost` صغير في آخر صف الفلاتر — والصف بيلفّ، فالزرار
                كان بينزل وسط الفلاتر ويختفي.** المؤسس نفسه مالقهوش وهو عارف
                إنه موجود. الميزة اتبنت وشغالة ١٠٠٪ وعمليًا مش موجودة.
                بقى فعلًا أساسيًا، وباسمه الكامل، وقبل «وزّع مهمة». */}
            {canWrite ? (
              <button
                type="button"
                className="btn sm accent"
                style={{ flexShrink: 0 }}
                aria-expanded={adding}
                onClick={() => {
                  setAssigning(false);
                  setAdding((v) => !v);
                }}
              >
                + مهمة جديدة
              </button>
            ) : null}
            {canAssign ? (
              <button
                type="button"
                className="btn sm ghost"
                aria-expanded={assigning}
                onClick={() => {
                  setAdding(false);
                  setAssigning((v) => !v);
                }}
              >
                وزّع مهمة
              </button>
            ) : null}
          </div>

          {adding ? <AddTaskForm onCancel={() => setAdding(false)} onSubmit={addTask} /> : null}
          {assigning ? (
            <AssignTaskForm
              depts={assignDepts}
              onCancel={() => setAssigning(false)}
              onSubmit={assignTask}
            />
          ) : null}

          {/* حالات الشاشة — كل حالة بتقول السبب وخطوة واحدة */}
          {loading && !data ? <LoadingState rows={4} /> : null}
          {fatal?.kind === "offline" ? <OfflineState onRetry={() => void loadTasks()} /> : null}
          {fatal?.kind === "forbidden" ? <NoPermissionState messageAr={fatal.messageAr} /> : null}
          {fatal?.kind === "error" ? (
            <ErrorState messageAr={fatal.messageAr} onRetry={() => void loadTasks()} />
          ) : null}

          {universe && !universe.ok ? (
            <ErrorState messageAr={universe.messageAr} onRetry={() => void loadTasks()} />
          ) : null}

          {universe?.ok && visible.length === 0 && query.trim() ? (
            <NoResultsState query={query.trim()} onClear={() => setQuery("")} />
          ) : null}

          {universe?.ok && visible.length === 0 && !query.trim() ? (
            <EmptyState
              title={emptyTitle(stat, source)}
              hint={emptyHint(stat, source)}
              {...(source !== "all"
                ? { action: { label: "شوف الكل", onClick: () => pickSource("all") } }
                : {})}
            />
          ) : null}

          {pageTasks.length > 0 ? (
            <div className="task-list">
              {pageTasks.map((t) => {
                const cardNote = notes[t.id] ?? null;
                return (
                  <TaskCard
                    key={t.id}
                    task={t}
                    primary={t.id === primaryTaskId}
                    busy={Boolean(busy[t.id])}
                    note={cardNote}
                    handoff={handoffFor(handoffs, t.id)}
                    waitingLabelAr={data?.queueLabels.get(t.id) ?? null}
                    mine={myId !== null && t.assignee_id === myId}
                    canWrite={canWrite}
                    canCancel={canCancel}
                    panel={panel?.taskId === t.id ? panel : null}
                    people={people}
                    closing={closing?.taskId === t.id ? closing.note : null}
                    onAction={() => void runAction(t)}
                    onOpenPanel={(kind) => openPanel(t, kind)}
                    onPanelChange={(next) => setPanel(next)}
                    onCancelPanel={() => setPanel(null)}
                    onConfirmPanel={() => void submitPanel(t)}
                    onRetryPeople={() => void loadPeople()}
                    onCloseNoteChange={(v) => setClosing({ taskId: t.id, note: v })}
                    onCancelClose={() => setClosing(null)}
                    onConfirmClose={() => void confirmClose(t)}
                    onToggleChecklist={(key, done) => void toggleChecklist(t, key, done)}
                    checklistBusy={(key) => Boolean(busy[`${t.id}:${key}`])}
                  />
                );
              })}
            </div>
          ) : null}

          {visible.length > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span className="t-meta">
                عرض <span className="num">{fmtNum(pageStart + 1)}</span>–
                <span className="num">{fmtNum(pageStart + pageTasks.length)}</span> من{" "}
                <span className="num">{fmtNum(visible.length)}</span>
              </span>
              <button
                type="button"
                className="btn sm ghost"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                السابق
              </button>
              <button
                type="button"
                className="btn sm ghost"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                التالي
              </button>
              {truncated && universe?.ok ? (
                // الشفافية بدل رقم ناقص بلا تفسير
                <span className="t-meta" style={{ color: "var(--warn)" }}>
                  وصلنا أول <span className="num">{fmtNum(universe.tasks.length)}</span> مهمة من{" "}
                  <span className="num">{fmtNum(universe.total)}</span> — استخدم الفلاتر
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* ===== (3) العمود الجانبي ===== */}
        <div className="desk-side">
          <HandoffsCard state={handoffs} onRetry={() => void loadHandoffs()} />
          <DraftsCard
            state={drafts}
            onRetry={() => void loadDrafts()}
            onSent={(draft, label) => {
              setDrafts((prev) =>
                prev && prev.ok ? { ok: true, rows: prev.rows.filter((d) => d.id !== draft.id) } : prev,
              );
              showToast(`اتسجّل إنك بعتّها على ${label}.`, null);
            }}
          />
          <OverdueCard state={data?.overdue ?? null} loading={loading && !data} />
          <ClosedCard state={closed} />
        </div>
      </div>

      {toast ? (
        <Toast
          text={toast.text}
          undoTaskId={toast.undoTaskId}
          busy={toast.undoTaskId ? Boolean(busy[toast.undoTaskId]) : false}
          onUndo={(id) => void undoLast(id)}
        />
      ) : null}
    </Shell>
  );
}

/* ─────────────────────── مكوّنات الشاشة ─────────────────────── */

/**
 * «اللي قفلتها» — المقفولة ما بتتمسحش، بس كانت بتختفي من «مكتبي» فالموظفة
 * تفتكر إنها راحت. القايمة للاطمئنان بس: عنوان وصاحب المهمة، بلا أزرار.
 */
function ClosedCard({ state }: { state: { ok: true; tasks: DeskTask[] } | { ok: false } | null }) {
  const [open, setOpen] = useState(false);
  const tasks = state && state.ok ? state.tasks : [];
  return (
    <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
      <button
        type="button"
        className="sec-h"
        style={{ background: "none", border: 0, padding: 0, cursor: "pointer", width: "100%", font: "inherit" }}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="sec-title">اللي قفلتها</span>
        <span className="sec-note">
          {state === null ? "…" : !state.ok ? "ما قدرناش نجيبها" : `${fmtNum(tasks.length)} مهمة — ${open ? "إخفاء" : "عرض"}`}
        </span>
      </button>
      {open && state?.ok ? (
        tasks.length === 0 ? (
          <span className="t-meta">لسه ما قفلتش حاجة. اللي بتقفلها بتيجي هنا وما بتتمسحش.</span>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {tasks.map((t) => (
              <div key={t.id} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span className="t-desc clip">✓ {t.title_ar}</span>
                {t.entity.label_ar ? <span className="t-meta clip">{t.entity.label_ar}</span> : null}
                {t.close_note_ar ? <span className="t-meta clip">ملاحظتك: {t.close_note_ar}</span> : null}
              </div>
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}

function StatCard({
  value,
  label,
  on,
  loading,
  failed,
  onClick,
}: {
  value: number | null;
  label: string;
  on: boolean;
  loading: boolean;
  failed: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className={`stat${on ? " on" : ""}`} aria-pressed={on} onClick={onClick}>
      <div className="v">
        {/* ⛔ ممنوع نعرض صفر على رقم ما وصلش — الشرطة بتقول «مش معروف» */}
        {value === null ? <span className="num">—</span> : <span className="num">{fmtNum(value)}</span>}
      </div>
      <div className="l">
        {label}
        {failed ? " · مقدرناش نجيبها" : loading ? " · بيحمّل" : ""}
      </div>
    </button>
  );
}

function TaskCard({
  task,
  primary,
  busy,
  note,
  handoff,
  waitingLabelAr,
  mine,
  canWrite,
  canCancel,
  panel,
  people,
  closing,
  onAction,
  onOpenPanel,
  onPanelChange,
  onCancelPanel,
  onConfirmPanel,
  onRetryPeople,
  onCloseNoteChange,
  onCancelClose,
  onConfirmClose,
  onToggleChecklist,
  checklistBusy,
}: {
  task: DeskTask;
  primary: boolean;
  busy: boolean;
  note: Note | null;
  handoff: Handoff | null;
  /** «في الطابور من 25 دقيقة» — للمهام اللي جاية من طابور القسم */
  waitingLabelAr: string | null;
  /** المهمة مسنَدة ليّا أنا — الأفعال الشخصية بتظهر على أساسه */
  mine: boolean;
  canWrite: boolean;
  canCancel: boolean;
  panel: Panel | null;
  people: SideLoad<AssignablePerson>;
  closing: string | null;
  onAction: () => void;
  onOpenPanel: (kind: PanelKind) => void;
  onPanelChange: (next: Panel) => void;
  onCancelPanel: () => void;
  onConfirmPanel: () => void;
  onRetryPeople: () => void;
  onCloseNoteChange: (v: string) => void;
  onCancelClose: () => void;
  onConfirmClose: () => void;
  onToggleChecklist: (key: string, done: boolean) => void;
  checklistBusy: (key: string) => boolean;
}) {
  const blocked = task.state === "blocked";
  const chip = chipFor(task);
  const action = task.primary_action;
  // التذكير له مهلة تبريد في السيستم — الزرار بيتقفل بسببها بنص واضح
  const nudgeLocked = blocked && task.blocked_by ? !task.blocked_by.can_nudge : false;
  const secondary = secondaryActions(task, { mine, canWrite, canCancel });

  return (
    <div className={`card task-card${blocked ? " blocked" : ""}`}>
      <div className="task-head">
        <div className="sec-title clip" title={task.title_ar}>
          {task.title_ar}
        </div>
        <span className={`chip ${chip.tone}`}>
          <span className="bullet" />
          {chip.text}
        </span>
        {action ? (
          <button
            type="button"
            // الزرار بياخد قد نصّه بالظبط زي التصميم — مش بيتمدّد على عرض العمود
            style={{ justifySelf: "start" }}
            className={`btn sm ${primary ? "primary" : "ghost"}${busy ? " is-busy" : ""}`}
            disabled={busy || nudgeLocked}
            aria-busy={busy}
            onClick={onAction}
            title={nudgeLocked ? "ذكّرتهم من شوية — استنى شوية" : undefined}
          >
            {busy ? "ثانية…" : action.label_ar}
          </button>
        ) : (
          <span className="t-meta" style={{ justifySelf: "start" }}>
            مفيش فعل عليك دلوقتي
          </span>
        )}
      </div>

      {/* ⛔ `white-space: normal` كان بيلزق كل الأسطر في فقرة واحدة — والموظف
          اللي بيكتب خطوات مرقّمة في ملاحظته كان شغله بيتحوّل لكتلة نص.
          `pre-line` بيحترم الأسطر وبيلمّ المسافات الزيادة. */}
      {task.body_ar ? (
        <div className="t-desc" style={{ whiteSpace: "pre-line" }}>
          {task.body_ar}
        </div>
      ) : null}
      {task.claim_note_ar ? (
        <div className="t-meta">آخر تحديث منك: {task.claim_note_ar}</div>
      ) : null}

      <div className="task-foot">
        <span className={`src ${srcClass(task.source)}`}>{task.source_label_ar}</span>
        {task.journey && task.journey.of > 0 ? (
          <>
            <div className="prog">
              <i style={{ width: `${progressPct(task.journey.step_no, task.journey.of)}%` }} />
            </div>
            <div className="t-meta clip">
              خطوة <span className="num">{fmtNum(task.journey.step_no ?? 0)}</span> من{" "}
              <span className="num">{fmtNum(task.journey.of)}</span>
              {task.journey.step_title_ar ? ` — ${task.journey.step_title_ar}` : ""}
            </div>
          </>
        ) : task.entity.label_ar ? (
          <div className="t-meta clip">{task.entity.label_ar}</div>
        ) : null}
        {/* 🔴 «مفيش مكان يدخلني الرحلة من هنا» — الموظفة كانت بترجع للّيدز وتدوّر على الاسم */}
        {task.journey ? (
          <Link to={`/journeys/${task.journey.id}`} className="btn sm ghost">
            افتح الرحلة
          </Link>
        ) : null}
        {blocked && task.blocked_by ? (
          <div className="t-meta clip">
            {`عند ${waitingWho(task)} ${task.blocked_by.since ? fmtRelative(task.blocked_by.since) : ""}`}
          </div>
        ) : null}
        {/* نص السيستم زي ما هو — الواجهة ما بتحسبش مدة انتظار من عندها */}
        {waitingLabelAr ? (
          <span className="chip ghost">
            <span className="bullet" />
            {withLatinDigits(waitingLabelAr)}
          </span>
        ) : null}
        {/* المهمة اللي اتفكّت — سطر أخضر جاي من إشعار التسليم نفسه */}
        {handoff && isUnlockNotice(handoff) ? (
          <span className="unlocked" style={{ marginRight: "auto" }}>
            {`${handoff.title_ar ?? "اتفكّت — دورك رجع"}${
              handoff.since_label_ar ? ` · ${handoff.since_label_ar}` : ""
            }`}
          </span>
        ) : null}
      </div>

      {note ? (
        <div
          className="t-meta"
          role="alert"
          style={{ color: note.tone === "error" ? "var(--danger)" : "var(--ok)" }}
        >
          {note.text}
        </div>
      ) : null}

      {/* ⛔ كلهم رمادي: الفعل الكحلي واحد بس في الشاشة */}
      {secondary.length > 0 && closing === null ? (
        <div className="task-acts">
          {secondary.map((a) => (
            <button
              key={a.kind}
              type="button"
              className={`btn sm ${a.kind === "cancel" ? "danger" : "ghost"}`}
              disabled={busy}
              aria-expanded={panel?.kind === a.kind}
              onClick={() => onOpenPanel(a.kind)}
            >
              {a.label}
            </button>
          ))}
        </div>
      ) : null}

      {panel ? (
        <ActionPanel
          task={task}
          panel={panel}
          busy={busy}
          people={people}
          onChange={onPanelChange}
          onCancel={onCancelPanel}
          onConfirm={onConfirmPanel}
          onRetryPeople={onRetryPeople}
        />
      ) : null}

      {closing !== null ? (
        <ClosePanel
          task={task}
          note={closing}
          busy={busy}
          onChange={onCloseNoteChange}
          onCancel={onCancelClose}
          onConfirm={onConfirmClose}
          onToggleChecklist={onToggleChecklist}
          checklistBusy={checklistBusy}
        />
      ) : null}
    </div>
  );
}

/**
 * أفعال الكارت الجانبية — **الموجود منها بس**.
 *
 * القاعدة: ما نعرضش فعل السيستم هيرفضه.
 *   • «اشتغلت فيها» ⇒ المهمة مستلَمة عندي أنا (المحرّك بيطلب `claimed`).
 *   • «أنا مستني حد» ⇒ مهمتي و`open` أو `claimed` (مش مقفولة أصلًا).
 *   • «سلّم لحد» ⇒ مهمتي وحيّة.
 *   • «ألغِ» ⇒ صلاحية `task.cancel` والمهمة لسه حيّة.
 */
function secondaryActions(
  task: DeskTask,
  who: { mine: boolean; canWrite: boolean; canCancel: boolean },
): { kind: PanelKind; label: string }[] {
  const out: { kind: PanelKind; label: string }[] = [];
  const alive = task.state === "open" || task.state === "claimed" || task.state === "blocked";
  if (!alive) return out;
  if (who.canWrite && who.mine) {
    if (task.state === "claimed") out.push({ kind: "progress", label: "اشتغلت فيها" });
    if (task.state !== "blocked") out.push({ kind: "block", label: "أنا مستني حد" });
    out.push({ kind: "handoff", label: "سلّم لحد" });
  }
  // ⛔ «ألغِ» لوحدها بتترسم «الغ» — الكسرة بتختفي في الحجم ده
  if (who.canCancel) out.push({ kind: "cancel", label: "إلغاء المهمة" });
  return out;
}

/** نصوص كل لوحة — مصدر واحد بدل ما تتنتر في الـJSX */
const PANEL_TEXT: Record<
  PanelKind,
  { label: string; placeholder: string; confirm: string; busy: string; multiline: boolean }
> = {
  progress: {
    label: "اكتب وصلت لفين — المهمة هتفضل مفتوحة عندك",
    placeholder: "كلّمت الأم ومردّتش — هعاود بكرة الصبح",
    confirm: "سجّل التحديث",
    busy: "بيتسجّل…",
    multiline: true,
  },
  block: {
    label: "مستني مين أو إيه بالظبط؟",
    placeholder: "رد قسم الحسابات على طلب التحويل",
    confirm: "قفلها على الانتظار",
    busy: "بيتقفل…",
    multiline: false,
  },
  handoff: {
    label: "سبب التسليم",
    placeholder: "خارجة إجازة بكرة والأسرة محتاجة رد",
    confirm: "سلّم",
    busy: "بيتسلّم…",
    multiline: false,
  },
  cancel: {
    label: "سبب الإلغاء — بيتسجّل في السجل",
    placeholder: "الأسرة سحبت الطلب",
    confirm: "أكّد الإلغاء",
    busy: "بيتلغي…",
    multiline: false,
  },
};

/** الفعل جاهز للإرسال؟ نفس شروط السيستم — الزرار ما يوعدش بحاجة هتترفض */
function panelReady(panel: Panel, text: string): boolean {
  if (text.length < MIN_REASON) return false;
  if (panel.kind === "handoff" && !panel.toUserId) return false;
  return true;
}

/** لوحة فعل واحدة جوّه الكارت — نفس شكل لوحة الإقفال */
function ActionPanel({
  task,
  panel,
  busy,
  people,
  onChange,
  onCancel,
  onConfirm,
  onRetryPeople,
}: {
  task: DeskTask;
  panel: Panel;
  busy: boolean;
  people: SideLoad<AssignablePerson>;
  onChange: (next: Panel) => void;
  onCancel: () => void;
  onConfirm: () => void;
  onRetryPeople: () => void;
}) {
  const text = PANEL_TEXT[panel.kind];
  const value = panel.text;
  const ready = panelReady(panel, value.trim());
  const fieldId = `panel-${panel.kind}-${task.id}`;
  const peopleId = `panel-to-${task.id}`;

  return (
    <div className="card flat action-panel">
      {panel.kind === "handoff" ? (
        <>
          <label className="t-meta" htmlFor={peopleId}>
            تسلّمها لمين؟
          </label>
          {people === null ? (
            <span className="t-meta">بنجيب فريقك…</span>
          ) : !people.ok ? (
            // ⛔ ممنوع قايمة فاضية بلا سبب
            <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
              {people.messageAr}{" "}
              <button type="button" className="link" onClick={onRetryPeople}>
                جرّب تاني
              </button>
            </span>
          ) : people.rows.length === 0 ? (
            <span className="t-meta">مفيش حد تاني نشط في قسمك تسلّمهاله.</span>
          ) : (
            <select
              id={peopleId}
              className="field"
              value={panel.toUserId ?? ""}
              disabled={busy}
              onChange={(e) => onChange({ ...panel, toUserId: e.target.value || null })}
            >
              <option value="">اختار الشخص…</option>
              {people.rows.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name_ar}
                </option>
              ))}
            </select>
          )}
        </>
      ) : null}

      <label className="t-meta" htmlFor={fieldId}>
        {text.label}
      </label>
      {text.multiline ? (
        <textarea
          id={fieldId}
          className="field"
          rows={2}
          value={value}
          disabled={busy}
          placeholder={text.placeholder}
          onChange={(e) => onChange({ ...panel, text: e.target.value })}
        />
      ) : (
        <input
          id={fieldId}
          className="field"
          value={value}
          disabled={busy}
          placeholder={text.placeholder}
          onChange={(e) => onChange({ ...panel, text: e.target.value })}
        />
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className={`btn sm ${panel.kind === "cancel" ? "danger" : "ghost"}${
            busy ? " is-busy" : ""
          }`}
          disabled={!ready || busy}
          aria-busy={busy}
          onClick={onConfirm}
        >
          {busy ? text.busy : text.confirm}
        </button>
        <button type="button" className="btn sm ghost" disabled={busy} onClick={onCancel}>
          رجوع
        </button>
        {!ready ? (
          <span className="t-meta">
            {panel.kind === "handoff" && !panel.toUserId
              ? "اختار الشخص واكتب السبب"
              : "اكتب سطر يوضّح — من غيره السيستم بيرفض"}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** ⛔ ممنوع إقفال أعمى — لازم ملاحظة إقفال أو بنود القايمة المطلوبة كلها مكتملة */
function ClosePanel({
  task,
  note,
  busy,
  onChange,
  onCancel,
  onConfirm,
  onToggleChecklist,
  checklistBusy,
}: {
  task: DeskTask;
  note: string;
  busy: boolean;
  onChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  onToggleChecklist: (key: string, done: boolean) => void;
  checklistBusy: (key: string) => boolean;
}) {
  const ready = canClose(task, note.trim());
  return (
    <div
      className="card flat"
      style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}
    >
      {task.checklist.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {task.checklist.map((item) => (
            <label
              key={item.key}
              style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--fs-sm)" }}
            >
              <input
                type="checkbox"
                checked={item.done}
                disabled={checklistBusy(item.key)}
                onChange={(e) => onToggleChecklist(item.key, e.target.checked)}
              />
              <span>{item.label_ar}</span>
              {item.required ? <span className="t-meta">مطلوب</span> : null}
            </label>
          ))}
        </div>
      ) : null}

      <label className="t-meta" htmlFor={`close-note-${task.id}`}>
        اكتب سطر يقول اتعمل إيه
      </label>
      <textarea
        id={`close-note-${task.id}`}
        value={note}
        rows={2}
        onChange={(e) => onChange(e.target.value)}
        placeholder="اتكلمت مع ولي الأمر واتفقنا على…"
        style={{
          font: "inherit",
          color: "var(--ink)",
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-control)",
          padding: "8px 10px",
          resize: "vertical",
        }}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          className={`btn sm primary${busy ? " is-busy" : ""}`}
          disabled={!ready || busy}
          aria-busy={busy}
          onClick={onConfirm}
        >
          {busy ? "بيتقفل…" : "اقفل المهمة"}
        </button>
        <button type="button" className="btn sm ghost" disabled={busy} onClick={onCancel}>
          إلغاء
        </button>
        {!ready ? (
          <span className="t-meta">اكتب سطر يقول اتعمل إيه، أو كمّل بنود القايمة، قبل ما تقفل</span>
        ) : null}
      </div>
    </div>
  );
}

function AddTaskForm({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (
    titleAr: string,
    bodyAr: string,
    priority: TaskPriority,
    slaHours: number | null,
  ) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("normal");
  // ⚠️ الافتراضي مهلة **مش** «بلا مهلة» — شوف الشرح فوق `addTask`
  const [slaHours, setSlaHours] = useState<number | null>(24);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (busy) return; // قفل ضد الدوس المزدوج
    if (title.trim().length < 2) {
      setError("اكتب عنوان المهمة (حرفين على الأقل)");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(title.trim(), body.trim(), priority, slaHours);
    } catch (err) {
      if (!isAbort(err)) setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  const field = {
    font: "inherit",
    color: "var(--ink)",
    background: "var(--surface)",
    border: "1px solid var(--line)",
    borderRadius: "var(--r-control)",
    padding: "8px 10px",
    width: "100%",
  } as const;

  return (
    <div className="card" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
      <span className="sec-title">مهمة أضفتها لنفسي</span>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="عنوان المهمة"
        style={field}
        aria-label="عنوان المهمة"
      />
      <input
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="تفاصيل (اختياري)"
        style={field}
        aria-label="تفاصيل المهمة"
      />

      {/* ⛔ كان صفًّا واحدًا: اللابل جنب القايمة، والقايمة بترفع محتواها
          فوق حدودها فالنص بيتقص من تحت. كل حقل بقى عمودًا كامل الطول. */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
          <label className="t-meta" htmlFor="add-priority">الأولوية</label>
          <select
            id="add-priority"
            className="field"
            style={{ height: 36, width: "100%" }}
            value={priority}
            disabled={busy}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
          >
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
          <label className="t-meta" htmlFor="add-sla">تخلص إمتى؟</label>
          <select
            id="add-sla"
            className="field"
            style={{ height: 36, width: "100%" }}
            value={slaHours === null ? "" : String(slaHours)}
            disabled={busy}
            onChange={(e) => setSlaHours(e.target.value === "" ? null : Number(e.target.value))}
          >
            {SLA_OPTIONS.map((o) => (
              <option key={o.label} value={o.hours === null ? "" : String(o.hours)}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error ? (
        <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      ) : null}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className={`btn sm ghost${busy ? " is-busy" : ""}`}
          disabled={busy}
          aria-busy={busy}
          onClick={() => void submit()}
        >
          {busy ? "بيتضاف…" : "إضافة"}
        </button>
        <button type="button" className="btn sm ghost" disabled={busy} onClick={onCancel}>
          إلغاء
        </button>
      </div>
    </div>
  );
}

/**
 * «وزّع مهمة» — للمنسق والمدير (`task.create_for_others`).
 *
 * ⚠️ قايمة الأشخاص بتيجي من `/tasks/assignable` بالقسم المختار، فالشاشة
 *    ما بتعرضش حد بره القسم أصلًا — والسيستم بيرفض كمان لو الطلب اتزوّر.
 * ⛔ ممنوع نعرض القايمة فاضية بلا سبب: الفشل بيتقال والتحميل بيتقال.
 */
function AssignTaskForm({
  depts,
  onCancel,
  onSubmit,
}: {
  depts: string[];
  onCancel: () => void;
  onSubmit: (input: {
    titleAr: string;
    bodyAr: string;
    assigneeId: string;
    assigneeName: string;
    dept: string;
    priority: TaskPriority;
    slaHours: number | null;
  }) => Promise<void>;
}) {
  const [dept, setDept] = useState(depts[0] ?? "");
  const [assigneeId, setAssigneeId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("normal");
  const [slaHours, setSlaHours] = useState<number | null>(24);
  const [people, setPeople] = useState<SideLoad<AssignablePerson>>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!dept) return;
    let alive = true;
    setPeople(null);
    setAssigneeId("");
    listAssignable(dept).then(
      (rows) => {
        if (alive) setPeople({ ok: true, rows });
      },
      (err: unknown) => {
        if (alive) setPeople({ ok: false, messageAr: errorMessageAr(err) });
      },
    );
    return () => {
      alive = false;
    };
  }, [dept, nonce]);

  const chosen = people?.ok ? people.rows.find((r) => r.id === assigneeId) : undefined;
  const ready = title.trim().length >= 2 && Boolean(chosen) && Boolean(dept);

  async function submit() {
    if (busy || !ready || !chosen) return; // قفل ضد الدوس المزدوج
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        titleAr: title.trim(),
        bodyAr: body.trim(),
        assigneeId: chosen.id,
        assigneeName: chosen.name_ar,
        dept,
        priority,
        slaHours,
      });
    } catch (err) {
      if (!isAbort(err)) setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card assign-form">
      <span className="sec-title">وزّع مهمة على واحد من فريقك</span>

      <div className="assign-grid">
        <label className="t-meta" htmlFor="assign-dept">
          القسم
        </label>
        <select
          id="assign-dept"
          className="field"
          value={dept}
          disabled={busy}
          onChange={(e) => setDept(e.target.value)}
        >
          {DEPT_OPTIONS.filter((d) => depts.includes(d.key)).map((d) => (
            <option key={d.key} value={d.key}>
              {d.label}
            </option>
          ))}
        </select>

        <label className="t-meta" htmlFor="assign-person">
          الشخص
        </label>
        {people === null ? (
          <span className="t-meta">بنجيب فريق القسم…</span>
        ) : !people.ok ? (
          <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
            {people.messageAr}{" "}
            <button type="button" className="link" onClick={() => setNonce((n) => n + 1)}>
              جرّب تاني
            </button>
          </span>
        ) : people.rows.length === 0 ? (
          <span className="t-meta">مفيش حساب نشط تاني في القسم ده.</span>
        ) : (
          <select
            id="assign-person"
            className="field"
            value={assigneeId}
            disabled={busy}
            onChange={(e) => setAssigneeId(e.target.value)}
          >
            <option value="">اختار الشخص…</option>
            {people.rows.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name_ar}
              </option>
            ))}
          </select>
        )}

        <label className="t-meta" htmlFor="assign-priority">
          الأولوية
        </label>
        <select
          id="assign-priority"
          className="field"
          value={priority}
          disabled={busy}
          onChange={(e) => setPriority(e.target.value as TaskPriority)}
        >
          {PRIORITY_OPTIONS.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>

        <label className="t-meta" htmlFor="assign-sla">
          المهلة
        </label>
        <select
          id="assign-sla"
          className="field"
          value={slaHours === null ? "" : String(slaHours)}
          disabled={busy}
          onChange={(e) => setSlaHours(e.target.value === "" ? null : Number(e.target.value))}
        >
          {SLA_OPTIONS.map((o) => (
            <option key={o.label} value={o.hours === null ? "" : String(o.hours)}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <input
        className="field"
        value={title}
        disabled={busy}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="عنوان المهمة"
        aria-label="عنوان المهمة"
      />
      <input
        className="field"
        value={body}
        disabled={busy}
        onChange={(e) => setBody(e.target.value)}
        placeholder="تفاصيل (اختياري)"
        aria-label="تفاصيل المهمة"
      />

      {error ? (
        <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      ) : null}

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className={`btn sm ghost${busy ? " is-busy" : ""}`}
          disabled={!ready || busy}
          aria-busy={busy}
          onClick={() => void submit()}
        >
          {busy ? "بيتوزّع…" : "وزّع"}
        </button>
        <button type="button" className="btn sm ghost" disabled={busy} onClick={onCancel}>
          إلغاء
        </button>
        <span className="t-meta">
          {chosen
            ? `${chosen.name_ar} هيشوفها في مكتبه، وبتبقى مسؤوليته أول ما يستلمها.`
            : "اختار الشخص واكتب العنوان"}
        </span>
      </div>
    </div>
  );
}

function HandoffsCard({ state, onRetry }: { state: SideLoad<Handoff>; onRetry: () => void }) {
  return (
    <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="sec-h">
        <span className="sec-title">
          تسليمات وصلتني
          {state && state.ok ? (
            <>
              {" ("}
              <span className="num">{fmtNum(state.rows.length)}</span>
              {")"}
            </>
          ) : null}
        </span>
      </div>
      {state === null ? <LoadingState rows={2} /> : null}
      {state && !state.ok ? <ErrorState messageAr={state.messageAr} onRetry={onRetry} /> : null}
      {state && state.ok && state.rows.length === 0 ? (
        <div className="t-desc">مفيش تسليمات جديدة</div>
      ) : null}
      {state && state.ok
        ? state.rows.map((h) => (
            <div key={h.id} className="side-block">
              <div className="t-body clip">{h.title_ar ?? "تسليم جديد"}</div>
              {h.body_ar ? <div className="t-desc">{h.body_ar}</div> : null}
              <div className="t-meta">
                {[deptLabelAr(h.origin_dept), h.since_label_ar].filter(Boolean).join(" · ")}
              </div>
            </div>
          ))
        : null}
    </div>
  );
}

function OverdueCard({ state, loading }: { state: Universe | null; loading: boolean }) {
  return (
    <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="sec-h">
        <span className="sec-title">مهامي المتأخرة</span>
      </div>
      {loading || !state ? <LoadingState rows={2} /> : null}
      {state && !state.ok ? <div className="t-desc">{state.messageAr}</div> : null}
      {state && state.ok && state.tasks.length === 0 ? (
        <div className="t-desc">مفيش متأخرات — تمام كده</div>
      ) : null}
      {state && state.ok
        ? state.tasks.slice(0, 5).map((t, i) => (
            <div
              key={t.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
                ...(i > 0
                  ? { marginTop: 4, borderTop: "1px solid var(--line)", paddingTop: 16 }
                  : {}),
              }}
            >
              <span className="chip late" style={{ flexShrink: 0 }}>
                <span className="bullet" />
                {t.sla_label_ar ?? "متأخرة"}
              </span>
              <span className="t-body clip">{t.entity.label_ar || t.title_ar}</span>
            </div>
          ))
        : null}
    </div>
  );
}

/**
 * الرسائل الجاهزة للإرسال.
 * ⚠️ نص مقفول: الرسالة **بتتجهّز** — السيستم ما بيبعتش لولي الأمر.
 *    زرار «تم الإرسال» **توثيق** إن إنسان بعت، مش إرسال.
 */
function DraftsCard({
  state,
  onRetry,
  onSent,
}: {
  state: SideLoad<OutboundDraft>;
  onRetry: () => void;
  onSent: (draft: OutboundDraft, viaLabel: string) => void;
}) {
  const [copied, setCopied] = useState<Record<string, boolean>>({});
  const [via, setVia] = useState<Record<string, SentVia>>({});
  const [notes, setNotes] = useState<Record<string, Note>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  // ⚠️ نفس قاعدة الشاشة: القفل متزامن عشان ضغطتين في نفس اللحظة
  //    ما يسجّلوش الإرسال مرتين
  const inFlight = useRef<Set<string>>(new Set());

  async function copy(draft: OutboundDraft) {
    try {
      const clip = navigator.clipboard;
      if (!clip?.writeText) throw new Error("clipboard unavailable");
      await clip.writeText(draft.body_ar);
      setCopied((c) => ({ ...c, [draft.id]: true }));
      setNotes((n) => ({ ...n, [draft.id]: { tone: "ok", text: "النص اتنسخ — الصقه في الجروب" } }));
    } catch (err) {
      // ⛔ ممنوع نسخة فاشلة تعدّي بالصمت — الموظف لازم يعرف إن مفيش حاجة اتنسخت
      console.error("[desk] نسخ نص الرسالة فشل", err);
      setNotes((n) => ({
        ...n,
        [draft.id]: { tone: "error", text: "مقدرناش ننسخ — حدّد النص وانسخه بإيدك" },
      }));
    }
  }

  async function markSent(draft: OutboundDraft) {
    if (inFlight.current.has(draft.id)) return; // قفل ضد الدوس المزدوج
    inFlight.current.add(draft.id);
    const channel = via[draft.id] ?? "whatsapp_group";
    setBusy((b) => ({ ...b, [draft.id]: true }));
    try {
      const updated = await markDraftSent(draft.id, channel, draft.version);
      const label =
        updated.sent_via_label_ar ??
        SENT_VIA_OPTIONS.find((o) => o.key === channel)?.label ??
        "القناة المختارة";
      onSent(draft, label);
    } catch (err) {
      if (!isAbort(err)) {
        setNotes((n) => ({ ...n, [draft.id]: { tone: "error", text: errorMessageAr(err) } }));
      }
    } finally {
      inFlight.current.delete(draft.id);
      setBusy((b) => ({ ...b, [draft.id]: false }));
    }
  }

  return (
    <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="sec-h">
        <span className="sec-title">
          رسائل بتتجهّز
          {state && state.ok ? (
            <>
              {" ("}
              <span className="num">{fmtNum(state.rows.length)}</span>
              {")"}
            </>
          ) : null}
        </span>
      </div>
      <div className="t-meta">النص بيتجهّز هنا وإنت اللي بتبعته — السيستم ما بيبعتش لولي الأمر.</div>

      {state === null ? <LoadingState rows={2} /> : null}
      {state && !state.ok ? <ErrorState messageAr={state.messageAr} onRetry={onRetry} /> : null}
      {state && state.ok && state.rows.length === 0 ? (
        <div className="t-desc">مفيش رسائل مستنية منك</div>
      ) : null}

      {state && state.ok
        ? state.rows.map((d) => {
            const note = notes[d.id];
            return (
              <div key={d.id} className="side-block">
                <div className="t-body clip">{d.template_name_ar}</div>
                {d.entity?.label_ar ? <div className="t-meta">{d.entity.label_ar}</div> : null}
                <DraftBody draft={d} />
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" className="btn sm ghost" onClick={() => void copy(d)}>
                    {copied[d.id] ? "اتنسخت" : "نسخ"}
                  </button>
                  <select
                    value={via[d.id] ?? "whatsapp_group"}
                    aria-label="اتبعتت على"
                    onChange={(e) => setVia((v) => ({ ...v, [d.id]: e.target.value as SentVia }))}
                    style={{
                      font: "inherit",
                      color: "var(--ink-2)",
                      background: "var(--surface)",
                      border: "1px solid var(--line)",
                      borderRadius: "var(--r-control)",
                      padding: "6px 8px",
                    }}
                  >
                    {SENT_VIA_OPTIONS.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className={`btn sm ghost${busy[d.id] ? " is-busy" : ""}`}
                    disabled={Boolean(busy[d.id])}
                    aria-busy={Boolean(busy[d.id])}
                    onClick={() => void markSent(d)}
                  >
                    {busy[d.id] ? "بيتسجّل…" : "تم الإرسال"}
                  </button>
                </div>
                {note ? (
                  <span
                    className="t-meta"
                    role="alert"
                    style={{ color: note.tone === "error" ? "var(--danger)" : "var(--ok)" }}
                  >
                    {note.text}
                  </span>
                ) : null}
              </div>
            );
          })
        : null}
    </div>
  );
}

/**
 * نص الرسالة للعرض.
 * الباك إند بيهرّب كل حاجة ومابيسيبش غير `<bdi>` (للمواعيد — بلا `dir`
 * عشان فخّ الترتيب). أي وسم تاني معناه إن حاجة اتغيّرت هناك ⇒ بنصرخ في
 * اللوج وبنعرض النص الخام بدل ما نحقن HTML مش متوقع.
 */
const SAFE_DRAFT_HTML = /^(?:[^<>]|<bdi>|<bdi dir="ltr">|<\/bdi>|<br\s?\/?>)*$/;

/**
 * ⛔ الفخّ اللي بيقلب المواعيد: `<bdi dir="ltr">` جواها حرف عربي بتخلّي
 *    «6م – 8م» تتقرا «م – 8م6» = الأسرة تحضر في وقت غلط. السيرفر بيمنعها،
 *    وده حزام تاني: لو حصلت، بنصرخ ونعرض النص الخام.
 */
const LTR_BDI_WITH_ARABIC = /<bdi dir="ltr">(?:(?!<\/bdi>)[\s\S])*[؀-ۿ]/;

function isSafeDraftHtml(html: string): boolean {
  return SAFE_DRAFT_HTML.test(html) && !LTR_BDI_WITH_ARABIC.test(html);
}

function DraftBody({ draft }: { draft: OutboundDraft }) {
  const style = {
    whiteSpace: "pre-wrap" as const,
    background: "var(--line-2)",
    borderRadius: "var(--r-control)",
    padding: "8px 10px",
    maxHeight: 120,
    overflowY: "auto" as const,
  };
  if (draft.body_html && isSafeDraftHtml(draft.body_html)) {
    return (
      <div
        className="t-desc"
        style={style}
        // النص مهرَّب من السيرفر — بس `<bdi>` بيعدّي، والتحقق فوق بيضمن كده
        dangerouslySetInnerHTML={{ __html: draft.body_html }}
      />
    );
  }
  if (draft.body_html) {
    console.error(
      "[desk] نص رسالة فيه وسوم مش متوقعة أو موعد بترتيب مقلوب — بنعرض الخام",
      draft.id,
    );
  }
  return (
    <div className="t-desc" style={style}>
      {draft.body_ar}
    </div>
  );
}

/** شريط التأكيد بعد أي فعل — ومعاه «تراجع» لو السيستم لسه سامح بيه */
function Toast({
  text,
  undoTaskId,
  busy,
  onUndo,
}: {
  text: string;
  undoTaskId: string | null;
  busy: boolean;
  onUndo: (taskId: string) => void;
}) {
  return (
    <div className="toast-dock">
      <div className="toast" role="status">
        <Icon name="check" size={14} stroke="#FFFFFF" strokeWidth={2.2} />
        {text}
        {undoTaskId ? (
          <button type="button" className="undo" disabled={busy} onClick={() => onUndo(undoTaskId)}>
            تراجع
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ─────────────────────── دوال مساعدة ─────────────────────── */

function isAbort(err: unknown): boolean {
  return err instanceof Error && err.name === "AbortError";
}

function classify(err: unknown): Fatal {
  if (err instanceof OfflineError) return { kind: "offline", messageAr: err.messageAr };
  if (err instanceof ApiError && err.status === 403) {
    return { kind: "forbidden", messageAr: err.messageAr };
  }
  return { kind: "error", messageAr: errorMessageAr(err) };
}

function toUniverse(res: PromiseSettledResult<TaskPage>): Universe {
  if (res.status === "fulfilled") {
    return { ok: true, tasks: res.value.tasks, total: res.value.meta?.total ?? res.value.tasks.length };
  }
  // اللوج اتكتب في `api/client` — هنا بنحوّل الفشل لرسالة تبان للموظف
  return { ok: false, messageAr: errorMessageAr(res.reason) };
}

/** لكل حالة لون واحد: مقفولة رمادي · متأخرة أحمر · قربت أصفر · في الوقت كحلي */
function chipFor(task: DeskTask): { tone: string; text: string } {
  if (task.state === "blocked") {
    return { tone: "auto", text: `مستنية ${waitingWho(task)}` };
  }
  if (task.is_overdue) {
    return { tone: "late", text: withLatinDigits(task.sla_label_ar ?? "متأخرة") };
  }
  if (task.sla_label_ar && task.due_at) {
    const hours = (new Date(task.due_at).getTime() - Date.now()) / 3_600_000;
    return { tone: hours <= 4 ? "soon" : "info", text: withLatinDigits(task.sla_label_ar) };
  }
  return { tone: "ghost", text: task.state === "claimed" ? "مستلمها إنت" : "بلا مهلة" };
}

/** الليبل المخزّن ساعات بيبدأ بـ«مستنية» أصلًا — منع «مستنية مستنية» */
function waitingWho(task: DeskTask): string {
  const raw = (task.blocked_by?.waiting_on_label_ar ?? "").trim();
  const dept = deptLabelAr(task.blocked_by?.waiting_on_dept);
  const label = raw || dept || "قسم تاني";
  return label.startsWith("مستنية ") ? label.slice("مستنية ".length) : label;
}

function srcClass(source: TaskSource): string {
  if (source === "journey") return "jrn";
  if (source === "event") return "evt";
  if (source === "assigned") return "asg";
  return "";
}

function progressPct(step: number | null, of: number): number {
  if (!step || of <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((step / of) * 100)));
}

/** الإقفال بدليل: ملاحظة مكتوبة **أو** بنود القايمة المطلوبة كلها مكتملة */
function canClose(task: DeskTask, note: string): boolean {
  if (note.length > 0) return true;
  const required = task.checklist.filter((c) => c.required);
  return required.length > 0 && required.every((c) => c.done);
}

/** إشعار تسليم مربوط بمهمة معيّنة — بيغذّي سطر «اتفكّت» الأخضر */
function handoffFor(state: SideLoad<Handoff>, taskId: string): Handoff | null {
  if (!state || !state.ok) return null;
  // 🔴 كان بيقرا `params.id` بس، والسيستم بيكتب `params.task`
  //    (`services/tasks.py::task_link`) ⇒ السطر الأخضر «اتفكّت — دورك رجع»
  //    عمره ما ظهر. المقارنة بقت على نفس مفتاح السيستم.
  return state.rows.find((h) => taskIdOfLink(h.deep_link) === taskId) ?? null;
}

function isUnlockNotice(h: Handoff): boolean {
  return (h.title_ar ?? "").startsWith("اتفكّت");
}

const SECTION_TITLES: Record<StatKey, string> = {
  open: "مهامي بالترتيب",
  overdue: "مهامي المتأخرة",
  waiting: "مستنية غيري",
  queue: "طابور فريقي",
};

function emptyTitle(stat: StatKey, source: SourceFilter): string {
  if (source !== "all") return "مفيش مهام في الفلتر ده";
  if (stat === "overdue") return "مفيش متأخرات";
  if (stat === "waiting") return "مفيش حاجة مستنية غيرك";
  if (stat === "queue") return "طابور فريقك فاضي";
  return "مفيش مهام دلوقتي";
}

function emptyHint(stat: StatKey, source: SourceFilter): string {
  if (source !== "all") return "جرّب فلتر تاني";
  if (stat === "overdue") return "كل حاجة في ميعادها";
  if (stat === "waiting") return "مفيش مهمة واقفة على قسم تاني";
  if (stat === "queue") return "كل طلبات القسم اتسلّمت — تمام كده";
  return "خلّصت كل حاجة النهارده";
}

/** بحث متسامح: بيشيل التشكيل ويوحّد الألفات والهاء والياء */
function normalizeAr(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[ً-ْٰ]/g, "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/\s+/g, " ");
}

/** حارس: أي رقم عربي-هندي جاي من بره بيتحوّل للاتيني — قاعدة مقفولة */
function withLatinDigits(text: string): string {
  return text.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}

function trimDot(text: string): string {
  return text.replace(/\s*[.。]\s*$/, "");
}
